import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { monitoringApi, Classification, MonitoringApiError, ListParams } from '../../../../services/monitoringApi';

export interface MonitoringListFilters {
    riskLevel: string;
    active: string;
    search: string;
    personName: string;
}

export default function useMonitoringList() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<Classification[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    const [filters, setFilters] = useState<MonitoringListFilters>({
        riskLevel: '',
        active: 'true',
        search: '',
        personName: '',
    });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params: ListParams = {
                page,
                pageSize,
                riskLevel: filters.riskLevel || undefined,
                active: filters.active === '' ? undefined : filters.active === 'true',
                search: filters.search || undefined,
                personName: filters.personName || undefined,
            };
            const result = await monitoringApi.list(params);
            setRows(result.data?.items || []);
            setTotal(result.data?.total || 0);
        } catch (e: unknown) {
            if (e instanceof MonitoringApiError) {
                if (e.status === 403) { navigate('/denied'); return; }
                if (e.status === 401) { navigate('/login'); return; }
            }
            toast.error('Erro ao carregar lista de monitoramento');
            setRows([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, pageSize, filters, navigate, refreshKey]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const refresh = () => setRefreshKey(k => k + 1);

    const updateFilter = (key: keyof MonitoringListFilters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({ riskLevel: '', active: '', search: '', personName: '' });
        setPage(1);
        setRefreshKey(k => k + 1);
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return {
        rows,
        total,
        page,
        setPage,
        pageSize,
        totalPages,
        isLoading,
        initialLoading,
        filters,
        updateFilter,
        clearFilters,
        refresh,
    };
}
